import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, FileText, ChevronDown, MapPin } from "lucide-react";
import { IMAGES, PHONE_TEL, WA_LINK } from "./data";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const bgScale = useTransform(scrollY, [0, 900], [1.08, 1.22]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="home" data-testid="hero-section" className="relative flex min-h-screen flex-col justify-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="KARM INDUSTRIES bio pellet manufacturing plant in Katni"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-[#050B07]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050B07]/85 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="container relative z-10 mx-auto px-4 pt-28 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-6 flex items-center gap-3 font-mono2 text-xs font-bold uppercase tracking-[0.3em] text-emerald-400"
          data-testid="hero-eyebrow"
        >
          <span className="inline-block h-px w-10 bg-emerald-400" />
          Bio Pellet Manufacturing · Katni, MP
        </motion.p>

        <h1 className="font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl" data-testid="hero-headline">
          <MaskedLine delay={0.3}>KARM</MaskedLine>
          <MaskedLine delay={0.45}>
            <span className="text-stroke-green">INDUSTRIES</span>
          </MaskedLine>
        </h1>

        <div className="mt-5 font-display text-2xl font-bold uppercase tracking-wide text-emerald-400 sm:text-3xl lg:text-4xl" data-testid="hero-subheadline">
          <MaskedLine delay={0.65}>Premium Bio Pellets</MaskedLine>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-4 font-mono2 text-sm uppercase tracking-[0.25em] text-slate-300 sm:text-base"
          data-testid="hero-tagline"
        >
          Green Fuel <span className="text-emerald-500">|</span> Clean Future
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap"
        >
          <a
            href="#quotation"
            data-testid="hero-get-quotation-button"
            className="group flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-4 text-sm font-bold uppercase tracking-wider text-[#050B07] transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_36px_rgba(16,185,129,0.45)]"
          >
            <FileText className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
            Get Quotation
          </a>
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="flex items-center justify-center gap-2 rounded-full border border-emerald-400/60 bg-emerald-950/40 px-8 py-4 text-sm font-bold uppercase tracking-wider text-emerald-300 backdrop-blur-sm transition-all duration-300 hover:border-emerald-300 hover:bg-emerald-900/50 hover:text-emerald-200"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp Us
          </a>
          <a
            href={PHONE_TEL}
            data-testid="hero-call-button"
            className="flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-all duration-300 hover:border-white/60 hover:bg-white/10"
          >
            <Phone className="h-4 w-4" />
            Call Now
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs font-medium uppercase tracking-widest text-slate-400"
          data-testid="hero-trust-strip"
        >
          <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> 3 Premium Grades</span>
          <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Pan-India Bulk Supply</span>
          <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-emerald-400" /> Katni, Madhya Pradesh</span>
        </motion.div>
      </motion.div>

      <motion.a
        href="#products"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-emerald-400"
        aria-label="Scroll to rates"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="h-7 w-7" />
        </motion.div>
      </motion.a>
    </section>
  );
};
